import { openDB, DBSchema, IDBPDatabase } from 'idb';
import { projectApi } from './projectApi';
import { API_BASE } from '../config';

interface BongaiDB extends DBSchema {
  canvasCache: {
    key: string;
    value: {
      projectId: string;
      version: number;
      nodes: any[];
      edges: any[];
      groups: any[];
      viewport: { x: number; y: number; zoom: number };
      updatedAt: number;
    };
  };
  agentCache: {
    key: string;
    value: {
      sessionId: string;
      projectId: string;
      version: number;
      messages: any[];
      currentModelId: string;
      updatedAt: number;
    };
  };
  syncQueue: {
    key: number;
    value: {
      id?: number;
      type: 'canvas' | 'agent';
      projectId: string;
      operation: 'save' | 'delete';
      data: any;
      version: number;
      timestamp: number;
    };
  };
  appData: {
    key: string;
    value: any;
  };
}

class UnifiedStorage {
  private db: IDBPDatabase<BongaiDB> | null = null;
  private syncDebounceTimer: number | null = null;
  private readonly DEBOUNCE_MS = 500;
  private initialized = false;
  private localVersions: Map<string, number> = new Map();
  private isOnline: boolean = typeof navigator !== 'undefined' ? navigator.onLine : true;
  private onlineRetryTimer: number | null = null;
  private syncing = false;
  private pendingSync = false;

  async init(): Promise<boolean> {
    if (this.initialized) return true;
    try {
      this.db = await openDB<BongaiDB>('bongai-unified', 2, {
        upgrade(db, oldVersion) {
          if (oldVersion < 1) {
            db.createObjectStore('canvasCache', { keyPath: 'projectId' });
            db.createObjectStore('agentCache', { keyPath: 'sessionId' });
            db.createObjectStore('syncQueue', { keyPath: 'id', autoIncrement: true });
          }
          if (oldVersion < 2) {
            db.createObjectStore('appData');
          }
        }
      });
      this.initialized = true;
      await this._migratePhase1Data();
      this._cleanupOldLocalStorage();
      this._setupOnlineListener();
      return true;
    } catch (e) {
      console.warn('[UnifiedStorage] IndexedDB init failed, falling back to localStorage:', e);
      this.initialized = false;
      return false;
    }
  }

  isReady(): boolean {
    return this.initialized && this.db !== null;
  }

  private _setupOnlineListener(): void {
    if (typeof window === 'undefined') return;
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.processSyncQueue();
    });
    window.addEventListener('offline', () => {
      this.isOnline = false;
    });
  }

  private async _migratePhase1Data(): Promise<void> {
    if (!this.db) return;
    try {
      const oldKeys = await this._getOldStorageKeys();
      for (const key of oldKeys) {
        const oldData = await this._getOldStorageData(key);
        if (!oldData) continue;

        if (key.startsWith('agent_messages_')) {
          const sessionId = key.replace('agent_messages_', '');
          if (Array.isArray(oldData)) {
            await this.db.put('agentCache', {
              sessionId,
              projectId: '',
              version: 0,
              messages: oldData,
              currentModelId: 'grsai-gemini-3.1-pro',
              updatedAt: Date.now(),
            });
          }
        } else {
          await this.db.put('appData', oldData, key);
        }
        await this._removeOldStorageData(key);
      }
    } catch (e) {
      console.warn('[UnifiedStorage] Phase 1 data migration failed:', e);
    }
  }

  private async _getOldStorageKeys(): Promise<string[]> {
    try {
      const oldDB = await openDB('bong_ai_db', 1);
      const keys = await oldDB.getAllKeys('app_data');
      oldDB.close();
      return keys as string[];
    } catch {
      return [];
    }
  }

  private async _getOldStorageData(key: string): Promise<any> {
    try {
      const oldDB = await openDB('bong_ai_db', 1);
      const data = await oldDB.get('app_data', key);
      oldDB.close();
      return data;
    } catch {
      return null;
    }
  }

  private async _removeOldStorageData(key: string): Promise<void> {
    try {
      const oldDB = await openDB('bong_ai_db', 1);
      await oldDB.delete('app_data', key);
      oldDB.close();
    } catch {
    }
  }

  private _stripNodeDataUrls(nodes: any[]): any[] {
    // Only strip base64 data URLs (data:...) to reduce storage size.
    // Keep URL references like /assets/... so that media is preserved.
    const dataUrlOnlyKeys = new Set(['dataUrl', 'thumbnailDataUrl']);
    return nodes.map(node => {
      const data = node.data;
      if (!data) return node;
      const stripped: any = {};
      let changed = false;
      for (const key of Object.keys(data)) {
        if (dataUrlOnlyKeys.has(key)) {
          changed = true;
          continue;
        }
        // Strip only if the value is a base64 data URL (starts with "data:")
        const val = data[key];
        if (typeof val === 'string' && val.startsWith('data:')) {
          changed = true;
          continue;
        }
        // Strip base64 entries inside arrays
        if (Array.isArray(val)) {
          const filtered = val.filter((v: any) => !(typeof v === 'string' && v.startsWith('data:')));
          if (filtered.length !== val.length) {
            changed = true;
            stripped[key] = filtered;
            continue;
          }
        }
        stripped[key] = data[key];
      }
      return changed ? { ...node, data: stripped } : node;
    });
  }

  async saveCanvas(projectId: string, data: {
    nodes: any[]; edges: any[]; groups: any[];
    viewport: { x: number; y: number; zoom: number };
  }): Promise<number> {
    await this.init();

    const strippedNodes = this._stripNodeDataUrls(data.nodes);

    const cacheKey = `canvas_${projectId}`;
    let currentVersion = this.localVersions.get(cacheKey) || 0;

    if (currentVersion === 0 && this.isReady()) {
      const cached = await this.db!.get('canvasCache', projectId);
      if (cached) currentVersion = cached.version;
    }

    const newVersion = currentVersion + 1;
    this.localVersions.set(cacheKey, newVersion);

    const strippedData = { ...data, nodes: strippedNodes };

    if (this.isReady()) {
      await this.db!.put('canvasCache', {
        projectId, version: newVersion, ...strippedData, updatedAt: Date.now(),
      });
    }

    // 不再写 localStorage 备份，避免配额超限（IndexedDB 已有足够容量）

    this.enqueueSync('canvas', projectId, strippedData, newVersion);
    return newVersion;
  }

  async getCanvas(projectId: string) {
    await this.init();
    if (this.isReady()) {
      const cached = await this.db!.get('canvasCache', projectId);
      if (cached) return cached;
    }
    return null;
  }

  async saveAgent(sessionId: string, data: {
    messages: any[]; currentModelId: string; projectId: string; activeReplyBuilder?: any;
  }): Promise<number> {
    await this.init();

    const cacheKey = `agent_${sessionId}`;
    let currentVersion = this.localVersions.get(cacheKey) || 0;

    if (currentVersion === 0 && this.isReady()) {
      const cached = await this.db!.get('agentCache', sessionId);
      if (cached) currentVersion = cached.version;
    }

    const newVersion = currentVersion + 1;
    this.localVersions.set(cacheKey, newVersion);

    if (this.isReady()) {
      await this.db!.put('agentCache', {
        sessionId, projectId: data.projectId, version: newVersion,
        messages: data.messages, currentModelId: data.currentModelId,
        updatedAt: Date.now(),
      });
    }

    // 不再写 localStorage 备份，避免配额超限（IndexedDB 已有足够容量）

    this.enqueueSync('agent', sessionId, data, newVersion);
    return newVersion;
  }

  async getAgent(sessionId: string) {
    await this.init();
    if (this.isReady()) {
      const cached = await this.db!.get('agentCache', sessionId);
      if (cached) return cached;
    }
    return null;
  }

  private async enqueueSync(
    type: 'canvas' | 'agent', id: string, data: any, version: number
  ): Promise<void> {
    if (this.isReady()) {
      await this.db!.add('syncQueue', {
        type, projectId: id, operation: 'save', data, version,
        timestamp: Date.now(),
      });
    }
    // 不再写 localStorage 备份，避免配额超限
    this.debouncedSync();
  }

  private debouncedSync() {
    if (this.syncDebounceTimer) clearTimeout(this.syncDebounceTimer);
    this.syncDebounceTimer = window.setTimeout(() => {
      this.processSyncQueue();
    }, this.DEBOUNCE_MS);
  }

  async processSyncQueue(): Promise<void> {
    if (!this.isReady()) return;
    if (!this.isOnline) return;
    if (this.syncing) {
      this.pendingSync = true;
      return;
    }
    this.syncing = true;

    try {
      while (true) {
        const queue = await this.db!.getAll('syncQueue');
        if (queue.length === 0) break;

        const latestByProject = new Map<string, BongaiDB['syncQueue']['value']>();
        for (const item of queue) {
          const key = `${item.type}_${item.projectId}`;
          const existing = latestByProject.get(key);
          if (!existing || item.version > existing.version) {
            latestByProject.set(key, item);
          }
        }

        for (const item of latestByProject.values()) {
          try {
            const newVersion = await this.syncToBackend(item);
            if (newVersion !== null) {
              await this._updateLocalVersion(item.type, item.projectId, newVersion);
            }
          } catch (error) {
            console.warn('Sync failed, will retry:', error);
            break;
          }
        }

        for (const item of queue) {
          await this.db!.delete('syncQueue', item.id!);
        }

        if (!this.pendingSync) break;
        this.pendingSync = false;
      }
    } finally {
      this.syncing = false;
      this.pendingSync = false;
    }

    try {
      const remaining = await this.db!.getAll('syncQueue');
      if (remaining.length === 0) {
        // 尝试清理旧的 localStorage 备份
        try { localStorage.removeItem('bongai_sync_queue_backup'); } catch {}
      }
      // 不再写 localStorage 备份，避免配额超限
    } catch (e) {
      console.warn('[UnifiedStorage] syncQueue cleanup failed:', e);
    }
  }

  private async _updateLocalVersion(type: 'canvas' | 'agent', id: string, version: number): Promise<void> {
    const cacheKey = `${type}_${id}`;
    this.localVersions.set(cacheKey, version);
    if (!this.db) return;
    if (type === 'canvas') {
      const cached = await this.db.get('canvasCache', id);
      if (cached) { cached.version = version; await this.db.put('canvasCache', cached); }
    } else {
      const cached = await this.db.get('agentCache', id);
      if (cached) { cached.version = version; await this.db.put('agentCache', cached); }
    }
  }

  private async syncToBackend(item: BongaiDB['syncQueue']['value']): Promise<number | null> {
    const remote = await fetch(`${API_BASE}/api/state/${item.projectId}/${item.type}`, {
      signal: AbortSignal.timeout(30_000),
    }).then(r => r.ok ? r.json() : null).catch(() => null);
    const remoteVersion = remote?.version ?? -1;

    const merged = remote?.data
      ? (item.type === 'canvas'
        ? this.mergeCanvasStates(remote.data, item.data)
        : this.mergeAgentStates(remote.data, item.data))
      : item.data;

    const response = await fetch(`${API_BASE}/api/state/save`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        project_id: item.projectId,
        state_type: item.type,
        data: merged,
        expected_version: remoteVersion,
      }),
      signal: AbortSignal.timeout(60_000),
    });

    if (!response.ok) {
      throw new Error(`Sync failed: ${response.status}`);
    }

    const result = await response.json();
    return result.version;
  }

  mergeCanvasStates(remote: any, local: any): any {
    const nodeMap = new Map();
    [...(remote?.nodes || []), ...(local?.nodes || [])].forEach(node => {
      const existing = nodeMap.get(node.id);
      if (!existing || (node.updatedAt || 0) > (existing.updatedAt || 0)) {
        nodeMap.set(node.id, node);
      }
    });

    const edgeMap = new Map();
    [...(remote?.edges || []), ...(local?.edges || [])].forEach(edge => {
      const edgeId = edge.id || `${edge.source}-${edge.target}`;
      const existing = edgeMap.get(edgeId);
      if (!existing || (edge.updatedAt || 0) > (existing.updatedAt || 0)) {
        edgeMap.set(edgeId, edge);
      }
    });

    const remoteViewportTime = remote?.viewport?.updatedAt || 0;
    const localViewportTime = local?.viewport?.updatedAt || 0;
    const mergedViewport = localViewportTime >= remoteViewportTime
      ? local?.viewport : remote?.viewport;

    return {
      nodes: this._stripNodeDataUrls(Array.from(nodeMap.values())),
      edges: Array.from(edgeMap.values()),
      groups: remote?.groups || local?.groups || [],
      viewport: mergedViewport || { x: 0, y: 0, zoom: 1 },
    };
  }

  mergeAgentStates(remote: any, local: any): any {
    const messageMap = new Map();
    [...(remote?.messages || []), ...(local?.messages || [])].forEach(msg => {
      const existing = messageMap.get(msg.id);
      if (!existing || (msg.updatedAt || msg.createdAt || 0) > (existing.updatedAt || existing.createdAt || 0)) {
        messageMap.set(msg.id, msg);
      }
    });
    return {
      messages: Array.from(messageMap.values()).sort(
        (a, b) => (a.sequence || 0) - (b.sequence || 0)
      ),
      currentModelId: local?.currentModelId || remote?.currentModelId || 'grsai-gemini-3.1-pro',
    };
  }

  async restoreSyncQueueFromBackup(): Promise<void> {
    try {
      const backup = localStorage.getItem('bongai_sync_queue_backup');
      if (!backup || !this.isReady()) return;
      const items = JSON.parse(backup);
      for (const item of items) {
        if (!item.id) continue;
        const existing = await this.db!.get('syncQueue', item.id);
        if (!existing) {
          await this.db!.add('syncQueue', item);
        }
      }
    } catch (e) {
      console.warn('[UnifiedStorage] Failed to restore sync queue from backup:', e);
    }
  }

  private autoSaveTimer: ReturnType<typeof setInterval> | null = null;
  private isDirty: boolean = false;
  private saving: boolean = false;
  private readonly autoSaveInterval: number = 60000;

  markDirty(): void {
    this.isDirty = true;
  }

  startAutoSave(
    projectId: string | null,
    getData: () => { nodes: any[]; connections: any[]; groups: any[] },
    getThumbnail: () => string
  ): void {
    this.stopAutoSave();
    this.autoSaveTimer = setInterval(async () => {
      if (this.saving || !projectId) return;
      const data = getData();
      if (data.nodes.length === 0) return;
      this.saving = true;
      try {
        // 项目缓存只写 IndexedDB，不再写 localStorage（避免配额超限）
        await this.saveProjectCache(projectId, data);
        await this.saveToBackend(projectId, data, getThumbnail());
        await this.saveCanvas(projectId, {
          nodes: data.nodes,
          edges: data.connections,
          groups: data.groups,
          viewport: { x: 0, y: 0, zoom: 1 },
        });
        this.isDirty = false;
      } catch (e) {
        console.error('[UnifiedStorage] Auto-save failed:', e);
      } finally {
        this.saving = false;
      }
    }, this.autoSaveInterval);
  }

  stopAutoSave(): void {
    if (this.autoSaveTimer) {
      clearInterval(this.autoSaveTimer);
      this.autoSaveTimer = null;
    }
  }

  async flush(
    projectId: string | null,
    data: { nodes: any[]; connections: any[]; groups: any[] },
    thumbnail?: string
  ): Promise<void> {
    await this.saveToIndexedDB('nodes', data.nodes);
    await this.saveToIndexedDB('connections', data.connections);
    await this.saveToIndexedDB('groups', data.groups);
    await this.saveEmergencyBackup(data.nodes, data.connections, data.groups);
    if (projectId) {
      // 项目缓存写入 IndexedDB（不再写 localStorage，避免配额超限）
      await this.saveProjectCache(projectId, data);
      await this.saveCanvas(projectId, {
        nodes: data.nodes,
        edges: data.connections,
        groups: data.groups,
        viewport: { x: 0, y: 0, zoom: 1 },
      });
    }
  }

  async saveToIndexedDB(key: string, data: any): Promise<void> {
    await this.init();
    if (this.isReady()) {
      await this.db!.put('appData', data, key);
    }
  }

  async loadFromIndexedDB<T>(key: string): Promise<T | undefined> {
    await this.init();
    if (this.isReady()) {
      return await this.db!.get('appData', key) as T | undefined;
    }
    return undefined;
  }

  saveToLocalStorage(key: string, data: any): void {
    try {
      localStorage.setItem(key, JSON.stringify(data));
    } catch (e) {
      // 配额超限时：清理旧的 bongai_project_cache_* 和其他临时数据后重试
      console.warn(`[UnifiedStorage] localStorage write failed for key "${key}", attempting cleanup...`);
      this._cleanupLocalStorage();
      try {
        localStorage.setItem(key, JSON.stringify(data));
      } catch (e2) {
        console.warn(`[UnifiedStorage] localStorage still full after cleanup, skipping "${key}"`);
      }
    }
  }

  /**
   * 清理 localStorage 中的旧项目缓存（已迁移到 IndexedDB）
   * 保留：用户设置、最后保存时间戳
   * 清理：bongai_project_cache_*（大块数据，已迁移到 IndexedDB）
   */
  private _cleanupLocalStorage(): void {
    const keysToClean: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('bongai_project_cache_')) {
        keysToClean.push(key);
      }
    }
    keysToClean.forEach(k => {
      try { localStorage.removeItem(k); } catch {}
    });
    if (keysToClean.length > 0) {
      console.log(`[UnifiedStorage] Cleaned up ${keysToClean.length} old project cache(s) from localStorage`);
    }
  }

  /**
   * 初始化时清理 localStorage 中所有已迁移到 IndexedDB 的大块数据
   * 包括：canvas备份、agent备份、syncQueue备份、project缓存
   */
  private _cleanupOldLocalStorage(): void {
    const prefixesToClean = [
      'bongai_canvas_',
      'bongai_agent_',
      'bongai_project_cache_',
      'bongai_sync_queue_backup',
      'bongai_emergency_nodes',
      'bongai_emergency_connections',
      'bongai_emergency_groups',
    ];
    const keysToClean: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && prefixesToClean.some(prefix => key.startsWith(prefix))) {
        keysToClean.push(key);
      }
    }
    keysToClean.forEach(k => {
      try { localStorage.removeItem(k); } catch {}
    });
    if (keysToClean.length > 0) {
      console.log(`[UnifiedStorage] Cleaned up ${keysToClean.length} old localStorage key(s) migrated to IndexedDB`);
    }
  }

  loadFromLocalStorage<T>(key: string): T | null {
    try {
      const raw = localStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  async cacheAsset(url: string): Promise<void> {
    if (!('caches' in window)) return;
    try {
      const cache = await caches.open('bong-ai-media-v1');
      const response = await cache.match(url);
      if (!response) {
        const fetchResponse = await fetch(url);
        if (fetchResponse.ok) {
          await cache.put(url, fetchResponse);
        }
      }
    } catch {}
  }

  async getCachedAsset(url: string): Promise<Response | undefined> {
    if (!('caches' in window)) return undefined;
    try {
      const cache = await caches.open('bong-ai-media-v1');
      return await cache.match(url);
    } catch {
      return undefined;
    }
  }

  async clearAssetCache(): Promise<void> {
    if (!('caches' in window)) return;
    try {
      await caches.delete('bong-ai-media-v1');
    } catch {}
  }

  async saveEmergencyBackup(nodes: any[], connections: any[], groups: any[]): Promise<void> {
    // 紧急备份写入 IndexedDB（不再写 localStorage，避免配额超限）
    try {
      await this.saveToIndexedDB('bongai_emergency_nodes', nodes);
      await this.saveToIndexedDB('bongai_emergency_connections', connections);
      await this.saveToIndexedDB('bongai_emergency_groups', groups);
    } catch (e) {
      console.warn('[UnifiedStorage] Emergency backup failed:', e);
    }
  }

  async loadEmergencyBackup<T = any>(): Promise<{ nodes: T[] | null; connections: T[] | null; groups: T[] | null }> {
    try {
      // 优先从 IndexedDB 读取
      const nodes = await this.loadFromIndexedDB<T[]>('bongai_emergency_nodes');
      const connections = await this.loadFromIndexedDB<T[]>('bongai_emergency_connections');
      const groups = await this.loadFromIndexedDB<T[]>('bongai_emergency_groups');
      if (nodes && nodes.length > 0) {
        return { nodes, connections: connections || null, groups: groups || null };
      }
      // 回退到 localStorage（兼容旧数据）
      const lsNodes = this.loadFromLocalStorage<T[]>('bongai_emergency_nodes');
      const lsConnections = this.loadFromLocalStorage<T[]>('bongai_emergency_connections');
      const lsGroups = this.loadFromLocalStorage<T[]>('bongai_emergency_groups');
      return { nodes: lsNodes, connections: lsConnections, groups: lsGroups };
    } catch {
      return { nodes: null, connections: null, groups: null };
    }
  }

  async saveProjectCache(projectId: string, data: { nodes: any[]; connections: any[]; groups: any[] }): Promise<void> {
    // 写入 IndexedDB（容量大，不会配额超限）
    await this.saveToIndexedDB(`bongai_project_cache_${projectId}`, data);
  }

  async loadProjectCache<T = any>(projectId: string): Promise<{ nodes: T[]; connections: T[]; groups: T[] } | null> {
    // 优先从 IndexedDB 读取
    const indexedData = await this.loadFromIndexedDB<{ nodes: T[]; connections: T[]; groups: T[] }>(`bongai_project_cache_${projectId}`);
    if (indexedData && indexedData.nodes && indexedData.nodes.length > 0) {
      return indexedData;
    }
    // 回退到 localStorage（兼容旧数据）
    return this.loadFromLocalStorage<{ nodes: T[]; connections: T[]; groups: T[] }>(`bongai_project_cache_${projectId}`);
  }

  // 后端保存防抖：避免每次节点变化都立即请求后端（Render 冷启动时累积大量请求）
  private _backendSaveTimer: ReturnType<typeof setTimeout> | null = null;
  private _backendSaveInProgress: Promise<void> | null = null;

  async saveToBackend(projectId: string, data: { nodes: any[]; connections: any[]; groups: any[] }, thumbnail?: string): Promise<void> {
    if (!projectId) return;

    // 如果有正在进行的保存，等它完成再决定是否需要再保存一次
    if (this._backendSaveInProgress) {
      // 标记有新数据待保存，不做并发请求
      this._pendingBackendData = { projectId, data, thumbnail };
      return;
    }

    // 防抖：500ms 内多次调用只执行最后一次
    if (this._backendSaveTimer) {
      clearTimeout(this._backendSaveTimer);
    }

    return new Promise<void>((resolve) => {
      this._backendSaveTimer = setTimeout(async () => {
        this._backendSaveTimer = null;
        this._backendSaveInProgress = this._doSaveToBackend(projectId, data, thumbnail)
          .finally(() => {
            this._backendSaveInProgress = null;
            // 如果期间有新数据待保存，再保存一次
            if (this._pendingBackendData) {
              const pending = this._pendingBackendData;
              this._pendingBackendData = null;
              this.saveToBackend(pending.projectId, pending.data, pending.thumbnail).then(resolve);
            } else {
              resolve();
            }
          });
      }, 500);
    });
  }

  private _pendingBackendData: { projectId: string; data: any; thumbnail?: string } | null = null;

  private async _doSaveToBackend(projectId: string, data: { nodes: any[]; connections: any[]; groups: any[] }, thumbnail?: string): Promise<void> {
    try {
      const updateData: any = { workflow_data: data };
      if (thumbnail !== undefined && thumbnail !== '') {
        updateData.thumbnail = thumbnail;
      }
      // 不重试：失败就失败，本地缓存已经保存了数据
      // 重试会导致请求堆积 + AbortError 刷屏
      // 直接用 fetch 而非 projectApi.updateProject，避免默认重试 2 次导致请求堆积
      const { API_BASE } = await import('../config');
      const response = await fetch(`${API_BASE}/api/projects/${projectId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData),
        signal: AbortSignal.timeout(60_000),
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      try {
        const { useProjectStore } = await import('../stores/projectStore');
        const ps = useProjectStore.getState();
        if (ps.currentProjectId === projectId && ps.currentProject) {
          ps.updateWorkflowData(data.nodes, data.connections, data.groups);
        }
      } catch (syncErr) {
        console.warn('[UnifiedStorage] Sync workflow_data to projectStore failed:', syncErr);
      }
    } catch (e: any) {
      // 静默失败：本地缓存（IndexedDB）已经保存了数据，下次打开项目会恢复
      // 只在控制台输出警告，不刷屏
      if (e?.name === 'AbortError') {
        console.warn('[UnifiedStorage] Backend save aborted (likely Render cold start), local cache preserved');
      } else {
        console.warn('[UnifiedStorage] Backend save failed, local cache preserved:', e.message || e);
      }
    }
  }
}

export const unifiedStorage = new UnifiedStorage();
